// Rasterises a design to PNG by redrawing it onto an offscreen 2D context.
//
// We draw from the element model rather than screenshotting the DOM, so the
// output is resolution-independent and needs no third-party dependency. The
// background cases here must mirror `backgroundCss` in ./constants.

import { NOTE_STYLES, NOTE_FONT } from "./constants";

const roundRect = (ctx, x, y, w, h, r) => {
  const radius = Math.max(0, Math.min(r, Math.abs(w) / 2, Math.abs(h) / 2));
  ctx.beginPath();
  if (typeof ctx.roundRect === "function") {
    ctx.roundRect(x, y, w, h, radius);
    return;
  }
  ctx.moveTo(x + radius, y);
  ctx.arcTo(x + w, y, x + w, y + h, radius);
  ctx.arcTo(x + w, y + h, x, y + h, radius);
  ctx.arcTo(x, y + h, x, y, radius);
  ctx.arcTo(x, y, x + w, y, radius);
  ctx.closePath();
};

// Greedy word wrap that also honours explicit newlines.
const wrapLines = (ctx, text, maxWidth) => {
  const lines = [];
  for (const paragraph of String(text).split("\n")) {
    if (!paragraph) {
      lines.push("");
      continue;
    }
    let current = "";
    for (const word of paragraph.split(/\s+/)) {
      const candidate = current ? `${current} ${word}` : word;
      if (ctx.measureText(candidate).width <= maxWidth || !current) {
        current = candidate;
      } else {
        lines.push(current);
        current = word;
      }
    }
    if (current) lines.push(current);
  }
  return lines;
};

const drawText = (ctx, el, { x, y, w, h, font, color, padding = 0 }) => {
  ctx.font = font;
  ctx.fillStyle = color;
  ctx.textBaseline = "top";
  ctx.textAlign = el.align || "left";

  const innerW = w - padding * 2;
  const lineHeight = el.fontSize * 1.35;
  const lines = wrapLines(ctx, el.text || "", innerW);

  const anchorX =
    el.align === "center" ? x + w / 2 : el.align === "right" ? x + w - padding : x + padding;

  lines.forEach((line, i) => {
    const lineY = y + padding + i * lineHeight;
    if (lineY > y + h) return; // clip overflow rather than bleed outside
    ctx.fillText(line, anchorX, lineY);
  });
};

const loadImage = (src) =>
  new Promise((resolve) => {
    if (!src) return resolve(null);
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null);
    img.src = src;
  });

const paintBackground = (ctx, bg, width, height) => {
  const { kind, color, color2 } = bg;

  if (kind === "gradient") {
    const g = ctx.createLinearGradient(0, 0, width, height);
    g.addColorStop(0, color);
    g.addColorStop(1, color2);
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, width, height);
    return;
  }

  ctx.fillStyle = color;
  ctx.fillRect(0, 0, width, height);

  if (kind === "grid") {
    ctx.strokeStyle = color2;
    ctx.lineWidth = 1;
    ctx.beginPath();
    for (let x = 0; x <= width; x += 40) {
      ctx.moveTo(x + 0.5, 0);
      ctx.lineTo(x + 0.5, height);
    }
    for (let y = 0; y <= height; y += 40) {
      ctx.moveTo(0, y + 0.5);
      ctx.lineTo(width, y + 0.5);
    }
    ctx.stroke();
  } else if (kind === "dots") {
    ctx.fillStyle = color2;
    for (let y = 0; y <= height; y += 28) {
      for (let x = 0; x <= width; x += 28) {
        ctx.beginPath();
        ctx.arc(x, y, 2, 0, Math.PI * 2);
        ctx.fill();
      }
    }
  } else if (kind === "cork") {
    ctx.fillStyle = color2;
    for (let y = 0; y <= height; y += 22) {
      for (let x = 0; x <= width; x += 22) {
        ctx.beginPath();
        ctx.arc(x, y, 1.5, 0, Math.PI * 2);
        ctx.fill();
      }
    }
    for (let y = 9; y <= height; y += 13) {
      for (let x = 7; x <= width; x += 13) {
        ctx.beginPath();
        ctx.arc(x, y, 1, 0, Math.PI * 2);
        ctx.fill();
      }
    }
  }
};

const drawElement = (ctx, el, image) => {
  const { x, y, w, h } = el;

  ctx.save();
  ctx.globalAlpha = el.opacity ?? 1;

  // Rotate about the element's centre, then draw in local coordinates.
  ctx.translate(x + w / 2, y + h / 2);
  ctx.rotate(((el.rotation || 0) * Math.PI) / 180);
  ctx.translate(-w / 2, -h / 2);

  const stroked = el.stroke && el.stroke !== "transparent" && el.strokeWidth > 0;

  switch (el.type) {
    case "note": {
      const style = NOTE_STYLES[el.paletteIndex] || NOTE_STYLES[0];

      ctx.save();
      ctx.shadowColor = "rgba(15,23,42,0.28)";
      ctx.shadowBlur = 18;
      ctx.shadowOffsetY = 10;
      ctx.fillStyle = style.paper;
      roundRect(ctx, 0, 0, w, h, 4);
      ctx.fill();
      ctx.restore();

      // Magnet pin
      ctx.beginPath();
      ctx.arc(w / 2, 0, 11, 0, Math.PI * 2);
      const pin = ctx.createRadialGradient(
        w / 2 - 4,
        -4,
        1,
        w / 2,
        0,
        11
      );
      pin.addColorStop(0, "rgba(255,255,255,0.95)");
      pin.addColorStop(1, style.pin);
      ctx.fillStyle = pin;
      ctx.fill();

      drawText(ctx, el, {
        x: 0,
        y: 22,
        w,
        h: h - 30,
        font: `${el.fontSize}px ${NOTE_FONT}`,
        color: "#1f2937",
        padding: 12,
      });
      break;
    }

    case "text":
      drawText(ctx, el, {
        x: 0,
        y: 0,
        w,
        h,
        font: `${el.fontWeight} ${el.fontSize}px ${el.fontFamily}`,
        color: el.color,
        padding: 4,
      });
      break;

    case "rect":
      roundRect(ctx, 0, 0, w, h, el.radius || 0);
      if (el.fill !== "transparent") {
        ctx.fillStyle = el.fill;
        ctx.fill();
      }
      if (stroked) {
        ctx.strokeStyle = el.stroke;
        ctx.lineWidth = el.strokeWidth;
        ctx.stroke();
      }
      break;

    case "ellipse":
      ctx.beginPath();
      ctx.ellipse(w / 2, h / 2, Math.abs(w) / 2, Math.abs(h) / 2, 0, 0, Math.PI * 2);
      if (el.fill !== "transparent") {
        ctx.fillStyle = el.fill;
        ctx.fill();
      }
      if (stroked) {
        ctx.strokeStyle = el.stroke;
        ctx.lineWidth = el.strokeWidth;
        ctx.stroke();
      }
      break;

    case "line":
      ctx.beginPath();
      ctx.moveTo(0, h / 2);
      ctx.lineTo(w, h / 2);
      ctx.strokeStyle = el.stroke === "transparent" ? el.fill : el.stroke;
      ctx.lineWidth = el.strokeWidth || 2;
      ctx.lineCap = "round";
      ctx.stroke();
      break;

    case "image":
      if (image) {
        ctx.save();
        roundRect(ctx, 0, 0, w, h, el.radius || 0);
        ctx.clip();
        ctx.drawImage(image, 0, 0, w, h);
        ctx.restore();
      }
      if (stroked) {
        roundRect(ctx, 0, 0, w, h, el.radius || 0);
        ctx.strokeStyle = el.stroke;
        ctx.lineWidth = el.strokeWidth;
        ctx.stroke();
      }
      break;

    default:
      break;
  }

  ctx.restore();
};

/**
 * Render a design and trigger a PNG download.
 *
 * @param {object}   design            { width, height, background, elements }
 * @param {object}   opts
 * @param {function} opts.resolveNote  el => ({ text, paletteIndex }) for live note text
 * @param {number}   opts.scale        pixel density multiplier (default 2)
 * @param {string}   opts.fileName
 */
export async function exportPng(design, opts = {}) {
  const { width, height, background, elements } = design;
  const { resolveNote = () => null, scale = 2, fileName = "design.png" } = opts;

  const surface = document.createElement("canvas");
  surface.width = Math.round(width * scale);
  surface.height = Math.round(height * scale);

  const ctx = surface.getContext("2d");
  ctx.scale(scale, scale);

  paintBackground(ctx, background, width, height);

  const ordered = [...elements].sort((a, b) => (a.z || 0) - (b.z || 0));

  // Resolve every image up front so drawing stays synchronous and ordered.
  const images = await Promise.all(
    ordered.map((el) => (el.type === "image" ? loadImage(el.src) : null))
  );

  ordered.forEach((el, i) => {
    const live = el.type === "note" ? resolveNote(el) : null;
    drawElement(ctx, live ? { ...el, ...live } : el, images[i]);
  });

  const blob = await new Promise((resolve) => surface.toBlob(resolve, "image/png"));
  if (!blob) throw new Error("Could not render the design.");

  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  link.click();
  URL.revokeObjectURL(url);
}

/**
 * Read a File into a downscaled data URL so designs stay small enough to
 * save. Returns { src, width, height } in natural (post-scale) pixels.
 */
export function fileToScaledDataUrl(file, maxEdge) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("Could not read that file."));
    reader.onload = () => {
      const img = new Image();
      img.onerror = () => reject(new Error("That file isn't a readable image."));
      img.onload = () => {
        const ratio = Math.min(1, maxEdge / Math.max(img.width, img.height));
        const w = Math.round(img.width * ratio);
        const h = Math.round(img.height * ratio);

        const c = document.createElement("canvas");
        c.width = w;
        c.height = h;
        c.getContext("2d").drawImage(img, 0, 0, w, h);

        // PNG keeps transparency; JPEG is far smaller for photos.
        const type = file.type === "image/png" ? "image/png" : "image/jpeg";
        resolve({ src: c.toDataURL(type, 0.86), width: w, height: h });
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  });
}
